import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const MobileBottomNavigation: React.FC = () => {
    const location = useLocation();
    const { user, signOut } = useAuth();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);

    const isActive = (path: string) => location.pathname === path;

    // Close user menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSignOut = async () => {
        try {
            await signOut();
            setShowUserMenu(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const navItems = [
        {
            to: '/tasks',
            icon: '✅',
            label: 'Tasks',
            activeColor: 'text-green-600',
            activeBg: 'bg-green-50'
        },
        {
            to: '/courses',
            icon: '📚',
            label: 'Courses',
            activeColor: 'text-blue-600',
            activeBg: 'bg-blue-50'
        },
        {
            to: '/pomodoro',
            icon: '🍅',
            label: 'Pomodoro',
            activeColor: 'text-red-600',
            activeBg: 'bg-red-50'
        },
        // {
        //     to: '/projects',
        //     icon: '🚀',
        //     label: 'Projects',
        //     activeColor: 'text-purple-600',
        //     activeBg: 'bg-purple-50'
        // }
    ];

    return (
        <>
            {/* Bottom Navigation Bar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <div className="flex items-center justify-around px-2 py-2">
                    {navItems.map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`
                                flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200 min-w-0 flex-1
                                ${isActive(item.to)
                                    ? `${item.activeColor} ${item.activeBg}`
                                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                }
                            `}
                        >
                            <span className="text-xl mb-1">{item.icon}</span>
                            <span className={`text-xs font-medium truncate ${isActive(item.to) ? 'font-semibold' : ''}`}>
                                {item.label}
                            </span>
                        </Link>
                    ))}
                    
                    {/* User Profile Button */}
                    {user && (
                        <div className="relative flex-1 flex justify-center" ref={userMenuRef}>
                            <button
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                className={`
                                    flex flex-col items-center justify-center p-2 rounded-lg transition-all duration-200
                                    ${isActive('/settings/profile')
                                        ? 'text-gray-800 bg-gray-50'
                                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                                    }
                                `}
                            >
                                {user.photoURL ? (
                                    <img
                                        src={user.photoURL}
                                        alt={user.displayName || 'User'}
                                        className="w-6 h-6 rounded-full mb-1"
                                    />
                                ) : (
                                    <div className="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center text-white text-xs font-medium mb-1">
                                        {user.displayName?.[0] || user.email?.[0] || 'U'}
                                    </div>
                                )}
                                <span className="text-xs font-medium truncate">
                                    Profile
                                </span>
                            </button>

                            {/* User Menu Popup */}
                            {showUserMenu && (
                                <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl shadow-xl py-2 border border-gray-200">
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="font-medium text-gray-900 text-sm truncate">
                                            {user.displayName || 'User'}
                                        </p>
                                        <p className="text-gray-500 text-xs truncate">{user.email}</p>
                                    </div>
                                    <Link
                                        to="/settings/profile"
                                        onClick={() => setShowUserMenu(false)}
                                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="mr-3">⚙️</span>
                                        Settings
                                    </Link>
                                    <button
                                        onClick={handleSignOut}
                                        className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <span className="mr-3">🚪</span>
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom padding spacer for mobile to prevent content from being hidden behind the nav */}
            <div className="md:hidden h-20"></div>
        </>
    );
};

export default MobileBottomNavigation;